import React, { useMemo, useState } from 'react'
import { CSSObject } from '@emotion/react'
import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  Heading2,
  JustifyContent,
  Overline1,
  Text,
  TextAlign,
  Visible,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { Menu } from '@library/menu-service'
import { DeviceType, useDeviceType } from '@library/dom'

import { ActiveTab, CTA_HREF, CTA_LABEL, TRENDING_PILLS, WEEKLY_MENU } from './config'
import { trackCookedByUsClick, trackCookedByYouClick, trackThisWeeksMenuClick } from './tracking'
import {
  deriveCookedByUsRecipes,
  deriveCookedByYouRecipes,
  isNewAndLimitedEdition,
} from './deriveRecipes'
import { CWCCookedByYouRecipeCard } from './CWCCookedByYouRecipeCard'
import { CWCCookedByUsRecipeCard } from './CWCCookedByUsRecipeCard'
import { CWCNewTag } from './CWCNewTag'
import { CWCTrendingPill } from './CWCTrendingPill'
import { Carousel } from '../EndlessVarietySection/Carousel'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'

// The gap below the toggle differs by tab so both tab states land on the same overall height.
type ToggleWrapperProps = React.ComponentProps<typeof Box> & { activeTab: ActiveTab }

const ToggleWrapperBase = ({ activeTab: _activeTab, ...rest }: ToggleWrapperProps) => (
  <Box {...rest} />
)

const ToggleWrapper = withCitrusTheme(ToggleWrapperBase, (theme, props: ToggleWrapperProps) => [
  {
    display: 'flex',
    justifyContent: 'center',
    // Needed so TogglePill's width:100% resolves against the available width, not an auto one.
    alignSelf: 'stretch',
  } as CSSObject,
  // 28px isn't a valid SpaceScale value (the scale skips 7), so desktop stays raw px.
  theme.responsiveStyle(['24px', '24px', '24px', '28px'], ['paddingTop']) as CSSObject,
  // 23px isn't a valid SpaceScale value either, so tablet stays raw px too.
  props.activeTab === 'cookedByYou'
    ? (theme.responsiveStyle(['24px', '24px', '23px', '32px'], ['paddingBottom']) as CSSObject)
    : (theme.responsiveStyle(['72px', '72px', '50px', '32px'], ['paddingBottom']) as CSSObject),
])

const TogglePill = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    borderRadius: '999px',
    overflow: 'hidden',
  } as CSSObject,
  theme.responsiveStyle(['100%', '100%', '540px', '540px'], ['width']) as CSSObject,
  // Deliberately narrower than the CTA at mobile (346px vs 358px) — matches Figma, not a bug.
  theme.responsiveStyle(['346px', '346px', 'none', 'none'], ['maxWidth']) as CSSObject,
  // SpaceScale 1 is the closest token match — Figma's own coordinates are inconsistent here.
  theme.responsiveSpacing(1, ['paddingTop', 'paddingBottom']) as CSSObject,
  theme.responsiveStyle(['5px', '5px', '7px', '7px'], ['paddingLeft', 'paddingRight']) as CSSObject,
])

type ToggleTabProps = React.ComponentPropsWithoutRef<'button'> & {
  isSelected: boolean
  variant: 'cookedByYou' | 'cookedByUs'
}

const ToggleTabBase = ({ isSelected: _isSelected, variant: _variant, ...rest }: ToggleTabProps) => (
  <button {...rest} />
)

const ToggleTab = withCitrusTheme(
  ToggleTabBase,
  (theme, props) => {
    const selectedStyles: CSSObject = !props.isSelected
      ? {}
      : props.variant === 'cookedByYou'
      ? { backgroundColor: theme.color(Color.White), color: theme.color(Color.ColdGrey_800) }
      : { backgroundColor: theme.color(Color.Warning_900), color: theme.color(Color.White) }

    // Mobile-only quirk: the inactive "Cooked by you" segment is a fixed 148px width when
    // "Cooked by us" is selected, rather than sharing space equally like every other state.
    const isMobileFixedInactiveYou = props.variant === 'cookedByYou' && !props.isSelected

    return [
      {
        flex: '1 1 0',
        // minWidth:0 lets this shrink at narrow viewports instead of overflowing; overflow:hidden
        // clips gracefully if it still can't fit.
        minWidth: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        color: theme.color(Color.White),
        fontFamily: theme.tokens.FontFamilies.bold,
        fontWeight: 700,
        cursor: 'pointer',
        borderRadius: '999px',
        whiteSpace: 'nowrap',
      } as CSSObject,
      theme.responsiveStyle(['47px', '47px', '64px', '64px'], ['height']) as CSSObject,
      theme.responsiveSpacing([1, 1, 2, 2], ['gap']) as CSSObject,
      // Citrus FontSize scale: 1 = 14px/16px, 5 = 18px/24px.
      theme.responsiveFont([1, 1, 5, 5]) as CSSObject,
      selectedStyles,
      isMobileFixedInactiveYou
        ? (theme.responsiveStyle(
            ['0 0 148px', '0 0 148px', '1 1 0', '1 1 0'],
            ['flex'],
          ) as CSSObject)
        : {},
    ]
  },
  { omitProps: ['isSelected', 'variant'] },
)

// Tablet/desktop only (wrapped in <Visible> below) — mobile uses MobilePillGroups instead (see
// its comment for why).
const PillsRow = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  } as CSSObject,
  theme.responsiveSpacing(2, ['gap']) as CSSObject,
  theme.responsiveStyle(['358px', '358px', '507px', 'none'], ['maxWidth']) as CSSObject,
])

// Figma wants an explicit 2/3/2 row split at mobile, which natural flex-wrap can't guarantee (it
// depends on content width, not a fixed count) — so mobile renders 3 explicit rows instead of
// relying on wrapping.
const MobilePillGroups = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  } as CSSObject,
  theme.responsiveSpacing(2, ['gap']) as CSSObject,
])

const PillGroupRow = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  } as CSSObject,
  theme.responsiveSpacing(2, ['gap']) as CSSObject,
])

// 2/3/2 row split for the mobile-only grouped layout above, per Figma.
const TRENDING_PILL_MOBILE_ROWS = [
  TRENDING_PILLS.slice(0, 2),
  TRENDING_PILLS.slice(2, 5),
  TRENDING_PILLS.slice(5, 7),
]

// size prop (not fontSize/lineHeight here) drives typography — Text's own size-based styling
// would win a specificity fight against a plain CSSObject override.
const Description = withCitrusTheme(Text, (theme) => [
  {
    color: theme.color(Color.White),
    textAlign: 'center',
  } as CSSObject,
  theme.responsiveStyle(['358px', '358px', '540px', '530px'], ['maxWidth']) as CSSObject,
  // Only desktop needs extra padding to hit Figma's 34px box; mobile/tablet already land exactly.
  theme.responsiveStyle(['0px', '0px', '0px', '5px'], ['paddingTop', 'paddingBottom']) as CSSObject,
  // pre-line renders the copy's forced line break (see config.ts) on mobile/tablet; desktop wraps
  // naturally without it.
  theme.responsiveStyle(['pre-line', 'pre-line', 'normal', 'normal'], ['whiteSpace']) as CSSObject,
])

const DescriptionPrefix = withCitrusTheme('span', (theme) => ({
  fontFamily: theme.tokens.FontFamilies.bold,
}))

// Padding above/below the carousel differs by tab and breakpoint, tuned so both tab states land
// on the identical overall height Figma specifies — pixel-verified against Figma's screenshots,
// since its own per-breakpoint coordinates are internally inconsistent here. Don't recalculate
// these from raw frame coordinates without re-verifying against a screenshot.
type CarouselWrapperProps = React.ComponentProps<typeof Box> & { tab: ActiveTab }

const CarouselWrapperBase = ({ tab: _tab, ...rest }: CarouselWrapperProps) => <Box {...rest} />

const CarouselWrapper = withCitrusTheme(
  CarouselWrapperBase,
  (theme, props: CarouselWrapperProps) => [
    theme.responsiveStyle(
      props.tab === 'cookedByYou'
        ? ['24px', '24px', '24px', '24px']
        : ['50px', '50px', '52px', '31px'],
      ['paddingTop'],
    ) as CSSObject,
    theme.responsiveStyle(['24px', '24px', '32px', '44px'], ['paddingBottom']) as CSSObject,
    // Figma insets the first mobile card 16px from the row's left edge (both tabs) instead of
    // starting flush — tablet/desktop stay unpadded since they're centred via the carousel's own
    // align:center option.
    theme.responsiveSpacing([4, 4, 0, 0], ['paddingLeft']) as CSSObject,
  ],
)

// Figma only shows nav arrows at desktop; hidden via CSS here rather than a device-detection hook
// (which would hydration-mismatch, since those default to desktop on the server).
const CarouselNavVisibility = withCitrusTheme(Box, (theme) => ({
  '& .embla__prev, & .embla__next': theme.responsiveStyle(
    ['none', 'none', 'none', 'flex'],
    ['display'],
  ) as CSSObject,
}))

// ARIA APG tabs pattern: each tab needs an id + aria-controls pointing to the panel(s) it
// governs; each panel needs role="tabpanel" + aria-labelledby pointing back to its tab. The
// content row and carousel are two separate DOM subtrees per tab, so each tab controls two
// panel ids rather than one.
const tabId = (tab: ActiveTab) => `weekly-menu-tab-${tab}`
const contentPanelId = (tab: ActiveTab) => `weekly-menu-content-panel-${tab}`
const carouselPanelId = (tab: ActiveTab) => `weekly-menu-carousel-panel-${tab}`

export type CWCWeeklyMenuProps = {
  menu: Menu
  defaultTab?: ActiveTab
}

export const CWCWeeklyMenu = ({ menu, defaultTab = 'cookedByYou' }: CWCWeeklyMenuProps) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>(defaultTab)

  const cookedByYouRecipes = useMemo(() => deriveCookedByYouRecipes(menu), [menu])
  const cookedByUsRecipes = useMemo(() => deriveCookedByUsRecipes(menu), [menu])

  // Figma centres the carousel (peeking equally on both sides) at tablet/desktop, but keeps it
  // left-aligned at mobile. Unlike the nav-arrows visibility above, this only drives a post-mount
  // scroll position inside an effect — it never changes the server-rendered DOM/attributes, so
  // there's no hydration-mismatch risk in using a device-detection hook here.
  //
  // useDeviceType()'s TABLET bucket (640-767px) is still Citrus's mobile tier everywhere else in
  // this component — only its DESKTOP value starts at Citrus's real tablet cutoff (768px). So this
  // compares against DESKTOP, not MOBILE, to avoid centering the carousel for 640-767px visitors
  // while the rest of the section renders mobile-style layout for them.
  const deviceType = useDeviceType()
  const isMobileLayout = deviceType !== DeviceType.DESKTOP
  const carouselOptions = isMobileLayout
    ? { loop: false }
    : { loop: false, align: 'center' as const, containScroll: 'trimSnaps' as const }
  const startAtMiddleAvailableSnap = !isMobileLayout

  const selectCookedByYou = () => {
    setActiveTab('cookedByYou')
    trackCookedByYouClick()
  }

  const selectCookedByUs = () => {
    setActiveTab('cookedByUs')
    trackCookedByUsClick()
  }

  return (
    <section data-testid="weekly-menu">
      <Box
        bg={Color.ColdGrey_800}
        position={DisplayPosition.Relative}
        paddingV={[14, 14, 14, 20]}
        // Mobile-only: CTA-to-end-of-component is 48px, not the same 56px as the top padding.
        // Overrides paddingV's bottom value since Box has no separate paddingTop prop.
        paddingBottom={[12, 12, 14, 20]}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
        >
          <Heading2
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            textAlign={TextAlign.Center}
          >
            <Text
              display={Display.Block}
              fontFamily={FontFamily.ExtraBold}
              size={13}
              textAlign={TextAlign.Center}
              style={{ opacity: 0.65 }}
            >
              {WEEKLY_MENU.headingLine1}
            </Text>
            <Text
              display={Display.Block}
              fontFamily={FontFamily.ExtraBold}
              size={13}
              textAlign={TextAlign.Center}
            >
              {WEEKLY_MENU.headingLine2}
            </Text>
          </Heading2>
          <ToggleWrapper activeTab={activeTab}>
            <TogglePill role="tablist" aria-label="Recipe preview toggle">
              <ToggleTab
                id={tabId('cookedByYou')}
                role="tab"
                type="button"
                aria-selected={activeTab === 'cookedByYou'}
                aria-controls={`${contentPanelId('cookedByYou')} ${carouselPanelId('cookedByYou')}`}
                isSelected={activeTab === 'cookedByYou'}
                variant="cookedByYou"
                onClick={selectCookedByYou}
              >
                {WEEKLY_MENU.cookedByYouLabel}
              </ToggleTab>
              <ToggleTab
                id={tabId('cookedByUs')}
                role="tab"
                type="button"
                aria-selected={activeTab === 'cookedByUs'}
                aria-controls={`${contentPanelId('cookedByUs')} ${carouselPanelId('cookedByUs')}`}
                isSelected={activeTab === 'cookedByUs'}
                variant="cookedByUs"
                onClick={selectCookedByUs}
              >
                <CWCNewTag>{WEEKLY_MENU.cookedByUsTag}</CWCNewTag>
                {WEEKLY_MENU.cookedByUsLabel}
              </ToggleTab>
            </TogglePill>
          </ToggleWrapper>
          {/* Both panels stay mounted (hidden via display:none for the inactive tab) rather than
              conditionally rendered — a conditionally-rendered panel doesn't exist in the DOM,
              which leaves the inactive tab's aria-controls pointing at IDREFs that don't resolve
              to anything until after it's clicked. */}
          <Box
            role="tabpanel"
            id={contentPanelId('cookedByYou')}
            aria-labelledby={tabId('cookedByYou')}
            display={activeTab === 'cookedByYou' ? Display.Flex : Display.None}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
            gap={[3, 3, 3, 4]}
          >
            {/* size=[0,1,1,1] is Citrus's FontSize scale index for 12/16 -> 14/16, matching
                Figma's mobile/tablet+ pairing exactly (Overline1 forces uppercase + bold). */}
            <Overline1 color={Color.White} textAlign={TextAlign.Center} size={[0, 1, 1, 1]}>
              {WEEKLY_MENU.trendingLabel}
            </Overline1>
            {/* Citrus's real breakpoints are 640/768/1024/1280px, so array index 1 is the
                640-767px tier — "mobile" here means everything below the 768px tablet
                breakpoint (indices 0 and 1), not just index 0. */}
            <Visible display={['none', 'none', 'block', 'block']}>
              <PillsRow>
                {TRENDING_PILLS.map((label) => (
                  <CWCTrendingPill key={label}>{label}</CWCTrendingPill>
                ))}
              </PillsRow>
            </Visible>
            <Visible display={['block', 'block', 'none', 'none']}>
              <MobilePillGroups>
                {TRENDING_PILL_MOBILE_ROWS.map((row) => (
                  <PillGroupRow key={row.join('-')}>
                    {row.map((label) => (
                      <CWCTrendingPill key={label}>{label}</CWCTrendingPill>
                    ))}
                  </PillGroupRow>
                ))}
              </MobilePillGroups>
            </Visible>
          </Box>
          <Description
            role="tabpanel"
            id={contentPanelId('cookedByUs')}
            aria-labelledby={tabId('cookedByUs')}
            size={4}
            display={activeTab === 'cookedByUs' ? Display.Block : Display.None}
          >
            <DescriptionPrefix>{WEEKLY_MENU.cookedByUsDescriptionPrefix}</DescriptionPrefix>
            {WEEKLY_MENU.cookedByUsDescription}
          </Description>
        </Box>
        {/* Unlike the content-row panels above, the carousel can't just stay mounted-and-hidden:
            embla measures real geometry once at mount, and its one-shot "start centred" guard
            would fire against a hidden, zero-width container and never retry — breaking centring
            for whichever tab wasn't active on first render. So the carousel keeps unmounting/
            remounting on switch instead (a fresh instance gets real geometry each time), and the
            inactive tab gets an empty, hidden placeholder with its panel's id — enough for
            aria-controls to resolve without reviving the embla-while-hidden problem. */}
        {activeTab === 'cookedByYou' ? (
          <CarouselWrapper
            tab="cookedByYou"
            role="tabpanel"
            id={carouselPanelId('cookedByYou')}
            aria-labelledby={tabId('cookedByYou')}
          >
            <CarouselNavVisibility key="cookedByYou">
              <Carousel
                carouselAreaLabel="Cooked by you"
                options={carouselOptions}
                slideWidth="320px"
                showNavButtons
                autoHideNavButtons
                startAtMiddleAvailableSnap={startAtMiddleAvailableSnap}
              >
                {cookedByYouRecipes.map((recipe) => (
                  <CWCCookedByYouRecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    isNewAndLimitedEdition={isNewAndLimitedEdition(menu, recipe.id)}
                  />
                ))}
              </Carousel>
            </CarouselNavVisibility>
          </CarouselWrapper>
        ) : (
          <Box
            role="tabpanel"
            id={carouselPanelId('cookedByYou')}
            aria-labelledby={tabId('cookedByYou')}
            display={Display.None}
          />
        )}
        {activeTab === 'cookedByUs' ? (
          <CarouselWrapper
            tab="cookedByUs"
            role="tabpanel"
            id={carouselPanelId('cookedByUs')}
            aria-labelledby={tabId('cookedByUs')}
          >
            <CarouselNavVisibility key="cookedByUs">
              <Carousel
                carouselAreaLabel="Cooked by us"
                options={carouselOptions}
                slideWidth="320px"
                showNavButtons
                autoHideNavButtons
                centerSlidesWhenNotScrollable
                startAtMiddleAvailableSnap={startAtMiddleAvailableSnap}
              >
                {cookedByUsRecipes.map((recipe) => (
                  <CWCCookedByUsRecipeCard key={recipe.id} recipe={recipe} />
                ))}
              </Carousel>
            </CarouselNavVisibility>
          </CarouselWrapper>
        ) : (
          <Box
            role="tabpanel"
            id={carouselPanelId('cookedByUs')}
            aria-labelledby={tabId('cookedByUs')}
            display={Display.None}
          />
        )}
        <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
          <CWCLinkButton
            href={CTA_HREF}
            variant="secondary"
            onClick={trackThisWeeksMenuClick}
            // Dark background needs CWCLinkButton's dark colour mode, not the light default.
            colorMode="dark"
            fontSizeOverride={['15px', '15px', '15px', '16px']}
            maxWidthOverride={['358px', '358px', 'none', 'none']}
          >
            {CTA_LABEL}
          </CWCLinkButton>
        </Box>
      </Box>
    </section>
  )
}
